import React, { useRef, useEffect } from 'react'
import Chart, { ChartData } from 'chart.js/auto'
import styled from 'styled-components'
import { VolumeResponse } from '../../../types/API'

interface VolumeChartProps {
  data: VolumeResponse
}

const VolumeChart: React.FC<VolumeChartProps> = ({ data }) => {
  const lineData: Record<string, number> = {}
  let totalizer = 0

  for (const value of Object.values(data)) {
    for (const [date, { totalUsd }] of Object.entries(value)) {
      if (lineData[date]) {
        lineData[date] += totalUsd
      } else {
        lineData[date] = totalUsd
      }
      totalizer += totalUsd
    }
  }

  const processData = () => {
    let allDateLabels: string[] = []
    Object.values(data).forEach((dates) => {
      const currentDates = Object.keys(dates)
      allDateLabels = Array.from(new Set([...allDateLabels, ...currentDates]))
    })

    allDateLabels.sort()
    return { dateLabels: allDateLabels }
  }

  const { dateLabels } = processData()

  const chartData: ChartData<'line', number[], string> = {
    labels: dateLabels,
    datasets: [
      {
        type: 'line',
        fill: true,
        label: 'Volume',
        yAxisID: 'tasks-y-axis',
        data: Object.values(lineData),
        borderColor: 'rgba(111, 92, 230, 1)',
        backgroundColor: 'rgba(111, 92, 230, 0.4)',
        pointBackgroundColor: 'rgba(111, 92, 230, 1)',
        tension: 0.8,
        pointRadius: 7,
        pointHoverRadius: 10,
        pointHoverBackgroundColor: '#6f5ce6',
        pointHoverBorderColor: '#6f5ce6',
      },
    ],
  }

  // Ref for the canvas
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  // Ref for the Chart.js instance
  const chartRef = useRef<Chart | null>(null)

  // Function to create and render the chart
  const createChart = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      if (ctx) {
        chartRef.current = new Chart(ctx, {
          type: 'bar',
          data: chartData,
          options: {
            responsive: true,
            scales: {
              'tasks-y-axis': {
                beginAtZero: true,
              },
            },
          },
        })
      }
    }
  }

  // Function to destroy the chart when the component unmounts
  const destroyChart = () => {
    if (chartRef.current) {
      chartRef.current.destroy()
    }
  }

  // Create the chart when the component mounts
  useEffect(() => {
    createChart()

    // Clean up the chart when the component unmounts
    return () => {
      destroyChart()
    }
  }, [data])

  return (
    <>
      <h1>Volume {totalizer > 0 && <Tot>Total: {totalizer.toFixed(2)} USD</Tot>}</h1>
      <div>
        <canvas ref={canvasRef} />
      </div>
    </>
  )
}

const Tot = styled.p`
  font-size: 14px;
  display: inline-flex;
  background: #6f5ce6;
  padding: 5px 10px;
  border-radius: 22px;
  margin-left: 18px;
`

export default VolumeChart
